import { ActivatedRoute } from '@angular/router';
import { AfterViewInit, Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';

import { BasePage } from '../../base.page';
import {
    ClientRelation,
    ClientType,
    GetClientUseCase,
    IdInterface,
    UpdateClientUseCase,
    UserInterface,
    ValidateClientDataUseCase,
    ValidationClient,
    ValidationClientGroup,
    ValidationStatus,
} from '../../../../core';
import { ConfirmDialog } from '../../../ui/dialog/confirm/confirm.dialog';
import { ErrorDialog } from '../../../ui/dialog/error/error.dialog';
import { FullClient, Id, Validation } from '../../../domain/data';
import { LocalStorage } from '../../../client/helpers/local-storage';
import { MainRoutes } from '../../../main-routes.enum';
import { ParentEvents } from '../../../utils/parent-events.service';
import { ViewPage } from '../view.page';
import { ValidationReasonDialog } from '../../../ui/dialog/validation-reason/validation-reason.dialog';

@Component({
    templateUrl: './view-profile.page.html',
    styleUrls: ['./view-profile.page.scss'],
})
export class ViewProfilePage extends BasePage implements AfterViewInit, OnDestroy, OnInit {
    public ClientRelation: typeof ClientRelation = ClientRelation;
    public ClientType: typeof ClientType = ClientType;
    public MainRoutes: typeof MainRoutes = MainRoutes;
    public ValidationClientGroup: typeof ValidationClientGroup = ValidationClientGroup;
    public ValidationStatus: typeof ValidationStatus = ValidationStatus;

    private backSubscription: Subscription;

    public editing = false;
    public participant: FullClient;

    public validations: Map<ValidationClientGroup, ValidationClient> = new Map<ValidationClientGroup, ValidationClient>();

    @ViewChildren(MatExpansionPanel)
    panels: QueryList<MatExpansionPanel>;

    @ViewChild('formFullDataOne')
    public formFullDataOne: NgForm;

    @ViewChild('formFullDataTwo')
    public formFullDataTwo: NgForm;

    @ViewChild('formFullDataThree')
    public formFullDataThree: NgForm;

    constructor(
        parentEvents: ParentEvents,
        private dialog: MatDialog,
        private getClientUseCase: GetClientUseCase,
        private localStorage: LocalStorage,
        private route: ActivatedRoute,
        private updateClientUseCase: UpdateClientUseCase,
        private validateClientDataUseCase: ValidateClientDataUseCase,
        private viewPage: ViewPage
    ) {
        super(parentEvents);
        viewPage.showBackButton();
        this.backSubscription = viewPage.back.subscribe(() => {
            this.parentEvents.cancel();
        });
    }

    get user(): UserInterface {
        return this.viewPage.user;
    }

    get canEdit(): boolean {
        return this.user.canEdit && this.localStorage.clientIsEditable;
    }

    get canSave(): boolean {
        if (this.participant.type === ClientType.NATURAL_PERSON) {
            return this.formFullDataOne.valid && this.formFullDataTwo.valid && this.formFullDataThree.valid;
        } else if (this.participant.type === ClientType.MINOR || this.participant.type === ClientType.DISABLED) {
            return this.formFullDataOne.valid;
        } else if (this.participant.type === ClientType.LEGAL_PERSON) {
            return true;
        }

        return false;
    }

    get canValidate(): boolean {
        return this.validations && !Array.from(this.validations.values()).some((validation) => !validation.validation.status);
    }

    ngOnInit() {
        this.route.params.subscribe((params) => {
            if ('id' in params) {
                this.getClient(Id.fromString(params.id));
            }
        });
    }

    private getClient(code: IdInterface) {
        this.getClientUseCase.bind({ code }).subscribe((transaction) => {
            transaction.fold(
                (participant) => {
                    this.participant = participant as FullClient;
                    this.validations = new Map<ValidationClientGroup, ValidationClient>();
                    this.localStorage.validationClientGroups.map((group) => {
                        this.validations.set(group, new ValidationClient(group, new Validation(this.participant.id)));
                    });
                },
                (throwable) =>
                    this.dialog.open(ErrorDialog, {
                        data: { text: throwable.message },
                    })
            );
        });
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
        this.handleFormStatusChanges();
    }

    private handleFormStatusChanges(): void {
        const updatePanelNumber = (panels: QueryList<MatExpansionPanel>) => {
            panels.forEach((panel, i) => {
                document.querySelectorAll(`#${panel._headerId} .num`).forEach((item, j) => {
                    item.innerHTML = `${i + 1}.`;
                });

                document.querySelectorAll(`#${panel.id} .num`).forEach((item, j) => {
                    item.innerHTML = `${i + 1}.${j + 1}.`;
                });
            });
        };

        this.panels.changes.subscribe((panels: QueryList<MatExpansionPanel>) => updatePanelNumber(panels));
        updatePanelNumber(this.panels);
    }

    ngOnDestroy() {
        this.backSubscription.unsubscribe();
    }

    onAccordionChange() {
        this.parentEvents.resize();
    }

    onEdit() {
        this.editing = true;
    }

    onCancel() {
        const dialogRef = this.dialog.open(ConfirmDialog, {
            data: { text: 'dialog.loss-data' },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.editing = false;
                this.getClient(this.participant.encryptedCode);
            }
        });
    }

    onSubmit() {
        const dialogRef = this.dialog.open(ConfirmDialog, {
            data: { text: 'dialog.save-profile' },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.updateClientUseCase.bind({ client: this.participant }).subscribe((transaction) => {
                    if (transaction.isSuccess) {
                        this.editing = false;
                        this.parentEvents.cancel();
                    } else if (!transaction.isAuthError) {
                        this.dialog.open(ErrorDialog, {
                            data: { text: transaction.error },
                        });
                    }
                });
            }
        });
    }

    onRevise(validationClient: ValidationClient) {
        const dialogRef = this.dialog.open(ValidationReasonDialog, {
            data: {
                text: 'dialog.revise-client',
                validation: validationClient.validation,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                validationClient.validation.status = ValidationStatus.REVISE;
                this.nextStep();
            }
        });
    }

    private nextStep() {
        this.panels.map((panel) => panel.close());

        let complete = false;
        this.validations.forEach((validationClient) => {
            if (!complete && !validationClient.validation.status) {
                complete = true;

                switch (validationClient.group) {
                    case ValidationClientGroup.PERSONAL_DATA:
                        this.panels.toArray()[0].open();
                        break;
                    case ValidationClientGroup.PROFESSIONAL_DATA:
                        this.panels.toArray()[1].open();
                        break;
                    case ValidationClientGroup.PUBLIC_DATA:
                        this.panels.toArray()[2].open();
                        break;
                }
            }
        });
    }

    onValidate(validationClient: ValidationClient) {
        validationClient.validation.status = ValidationStatus.VALID;
        this.nextStep();
    }

    onValidateFinalize() {
        this.validateClientDataUseCase
            .bind({
                client: this.participant,
                validations: Array.from(this.validations.values()),
            })
            .subscribe((transaction) => {
                if (transaction.isSuccess) {
                    this.parentEvents.cancel();
                } else if (!transaction.isAuthError) {
                    this.dialog.open(ErrorDialog, {
                        data: { text: transaction.error },
                    });
                }
            });
    }
}
